import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type GuideStoryId } from '@redevicer/shared'
import BrandCategoryRail from './BrandCategoryRail'
import FeaturedListingsRail from './FeaturedListingsRail'
import GuideArticleModal from './GuideArticleModal'
import GuideStoriesSection from './GuideStoriesSection'
import FeedbackSurveyStrip from './FeedbackSurveyStrip'
import QuickSellMiniForm from './QuickSellMiniForm'
import ImeiHubSection from './ImeiHubSection'
import SmartPicksModal from './SmartPicksModal'
import TrustBar from './TrustBar'
import InstantValuation from './InstantValuation'
import TrustFaqSection from './TrustFaqSection'
import SellPathCardIcon from './SellPathCardIcon'
export interface BrowsePreset {
  q?: string
  condition?: string
  warranty?: '' | 'yes' | 'no'
  usage?: string
  focusSearch?: boolean
}

interface MarketplaceHomeProps {
  onOpenCatalog: (preset?: BrowsePreset) => void
  onSell: () => void
  onSellFast: () => void
  onOpenListingDetail: (listingId: number) => void
  onOpenAi?: () => void
  token?: string | null
  userPreview?: { email?: string; full_name?: string } | null
  currentUserId?: number
  onRequireLogin?: (intent?: 'offer' | 'exchange' | null, listingId?: number) => void
  onGuestAuthed?: (token: string, user: unknown) => void
  onListingCreated?: (listingId: number) => void
  onFlashMessage?: (message: string) => void
  /** Same as catalog — starts checkout or prompts login */
  onBuyNow?: (listingId: number) => void
  onViewAllArticles?: () => void
}

export default function MarketplaceHome({
  onOpenCatalog,
  onSell,
  onSellFast,
  onOpenListingDetail,
  onOpenAi,
  token = null,
  userPreview = null,
  currentUserId,
  onRequireLogin,
  onGuestAuthed,
  onListingCreated,
  onFlashMessage,
  onBuyNow,
  onViewAllArticles,
}: MarketplaceHomeProps) {
  const { t } = useTranslation()
  const [openArticle, setOpenArticle] = useState<GuideStoryId | null>(null)
  const [smartPicksOpen, setSmartPicksOpen] = useState(false)

  return (
    <div className="vc-home">
      <div className="vc-home__inset">
        <ImeiHubSection token={token} onRequireLogin={() => onRequireLogin?.()} />

        <section className="vc-hero vc-hero--banner" aria-labelledby="vc-hero-heading">
        <picture className="vc-hero-banner-media" aria-hidden="true">
          <source
            media="(max-width: 960px)"
            type="image/webp"
            srcSet="/images/final-hero-mobile.webp 1x, /images/final-hero-mobile@2x.webp 2x"
          />
          <source
            media="(max-width: 960px)"
            srcSet="/images/final-hero-mobile.png 1x, /images/final-hero-mobile@2x.png 2x"
          />
          <source
            type="image/webp"
            srcSet="/images/final-hero-2112.webp 2112w, /images/final-hero-4224.webp 4224w, /images/final-hero-6336.webp 6336w"
            sizes="100vw"
          />
          <img
            className="vc-hero-banner-img"
            src="/images/final-hero-mobile.webp"
            srcSet="/images/final-hero-mobile.webp 1x, /images/final-hero-mobile@2x.webp 2x, /images/final-hero-2112.webp 2112w, /images/final-hero-4224.webp 4224w, /images/final-hero-6336.webp 6336w"
            sizes="100vw"
            width={2322}
            height={1133}
            alt=""
            draggable={false}
            loading="eager"
            decoding="async"
            onDragStart={(e) => e.preventDefault()}
          />
        </picture>
        <div className="vc-hero-copy">
          <p className="vc-hero-tagline">{t('trust.hero.tagline')}</p>
          <h1 id="vc-hero-heading" className="vc-hero-title">
            {t('vc.heroTitle')}
          </h1>
          <div className="vc-hero-actions">
            <button type="button" className="vc-btn vc-btn-primary vc-btn-hero" onClick={() => onOpenCatalog()}>
              {t('vc.heroBrowse')}
            </button>
            <button type="button" className="vc-btn vc-btn-hero-secondary" onClick={onSell}>
              {t('vc.heroSell')}
            </button>
          </div>
        </div>
        </section>
      </div>

      <FeaturedListingsRail
        token={token}
        currentUserId={currentUserId}
        onRequireLogin={onRequireLogin}
        onBuyNow={onBuyNow}
        onOpenCatalog={onOpenCatalog}
        onOpenListing={onOpenListingDetail}
      />

      <section
        className="vc-section vc-section--brands vc-section--brands-wide"
        aria-labelledby="vc-cat-heading"
      >
        <h3 id="vc-cat-heading" className="vc-section-title">
          {t('vc.shopByBrand')}
        </h3>
        <p className="vc-section-lead">{t('vc.shopByBrandLead')}</p>
        <BrandCategoryRail onOpenCatalog={onOpenCatalog} />
      </section>

      <div className="vc-home__inset">
        <TrustBar />
        <InstantValuation onSell={onSell} />
      </div>

      <section
        className="vc-section vc-section--centered vc-section--articles vc-section--articles-wide"
        aria-labelledby="vc-news-heading"
      >
        <div className="vc-section-head">
          <h3 id="vc-news-heading" className="vc-section-title">
            {t('vc.newsHeading')}
          </h3>
        </div>
        <div className="vc-news-grid">
          <GuideStoriesSection onOpenFull={setOpenArticle} onViewAll={() => onViewAllArticles?.()} />
        </div>
      </section>

      <div className="vc-home__inset">
      <TrustFaqSection />

      <section className="vc-promo-duo vc-section--centered" aria-label={t('vc.promoDuoAria')}>
        <div className="vc-promo-duo__grid">
          <article className="vc-sell-banner-card vc-promo-duo__item" aria-labelledby="vc-sell-banner-heading">
            <div className="vc-sell-banner-visual" aria-hidden="true">
              <SellPathCardIcon kind="promo-quick-sell" />
            </div>
            <div className="vc-sell-banner-inner">
              <span className="vc-sell-banner-badge">{t('vc.headerSellFast')}</span>
              <h3 id="vc-sell-banner-heading" className="vc-sell-banner-title">
                {t('vc.sellBannerTitle')}
              </h3>
              <QuickSellMiniForm onContinueToSellFlow={onSellFast} />
            </div>
          </article>
          <FeedbackSurveyStrip />
        </div>
      </section>

      </div>

      {openArticle ? <GuideArticleModal storyId={openArticle} onClose={() => setOpenArticle(null)} /> : null}
      {smartPicksOpen ? (
        <SmartPicksModal
          onClose={() => setSmartPicksOpen(false)}
          onPick={(preset) => {
            setSmartPicksOpen(false)
            onOpenCatalog(preset)
          }}
        />
      ) : null}
    </div>
  )
}
